import { ALTERNATIVE_COMPONENTS, FOLLOW_ARTICLE_COMPONENTS_TYPES, FOLLOW_ARTICLE_TPA_IDS, getTpaNameById, INACCESSIBLE_COMPONENTS, INACCESSIBLE_COMPONENTS_ALTERNATIVES, isTpaComponent, REINSTALL_COMPONENTS_TYPES, RuleId, TPA_ALTERNATIVES } from '@wix/a11y-rules';
import { useTranslation } from '@wix/fe-essentials/react';
import { FixerType, RuleListStatus, ToggleType } from '@wix/rules-panel-ui';
import { isOptional } from '@wix/seo-common-core';
import { useAtom, useAtomValue } from 'jotai';
import React, { useCallback } from 'react';
import { currentTaskInfoAtom } from '../../atoms/bi-atoms';
import { inaccessibleComponentsSessionAtom, inaccessibleComponentsSessionPagesAtom } from '../../atoms/session-atoms';
import { BiCtaNames } from '../../bi/types';
import { InaccessibleComponentsRuleFixer } from '../../components/fixers/inaccessible-components-fixer/inaccessible-components-rule-fixer';
import { RuleDescriptionComponent } from '../../components/rule-description-component';
import { usePanelStateAtom } from '../../hooks/rules-state';
import { useA11yWizardAdapter } from '../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../hooks/use-bi-logger';
import { useNavigation } from '../../hooks/use-navigation';
import { useOpenArticleByEditorHost } from '../../hooks/use-open-article-by-editor-host';
import { getAxeBreakpointId } from '../../utils/get-axe-breakpoint-id';
import { getPageTypeForBi } from '../shared/bi-helpers';
import { shouldUpdateRuleStatus } from '../shared/helpers';
export const useInaccessibleComponentsRule = () => {
  const {
    t
  } = useTranslation();
  const navigateToPage = useNavigation();
  const {
    openSelectedArticle
  } = useOpenArticleByEditorHost();
  const {
    a11yWizardAdapter,
    editorHost
  } = useA11yWizardAdapter();
  const biLogger = useBiLogger();
  const [currentTaskInfo, setCurrentTaskInfo] = useAtom(currentTaskInfoAtom);
  const inaccessibleComponentsPages = useAtomValue(inaccessibleComponentsSessionPagesAtom);
  const {
    getGroupData,
    getRuleState,
    setRuleState
  } = usePanelStateAtom();
  const sessionComponents = useAtomValue(inaccessibleComponentsSessionAtom);
  const getIsNotApplicable = useCallback(ruleExecutionResults => {
    return ruleExecutionResults.every(_ref => {
      let {
        result
      } = _ref;
      return result == null || result.status === 'not-applicable';
    });
  }, []);
  const getInaccessibleComponents = useCallback(_ref2 => {
    let {
      ruleExecutionResult,
      pageId,
      routerId,
      isCrossPage
    } = _ref2;
    if (isOptional(pageId)) {
      throw new Error('pageId is required');
    }
    const elements = sessionComponents[pageId] ?? [];
    const notApplicable = getIsNotApplicable(ruleExecutionResult);
    if (notApplicable && elements.length === 0 && !inaccessibleComponentsPages.includes(pageId)) {
      return null;
    }
    const ruleState = getRuleState(pageId, RuleId.INACCESSIBLE_COMPONENTS);
    const {
      analysisState,
      itemType
    } = getGroupData(pageId ?? '') ?? {};
    if (isOptional(itemType)) {
      throw new Error('itemType is required');
    }
    const uniqueElementsMap = new Map();
    ruleExecutionResult.forEach(execResult => {
      const {
        inaccessibleComponents
      } = execResult.result;
      inaccessibleComponents.forEach(comp => {
        const elementId = comp.id;
        if (!uniqueElementsMap.has(elementId)) {
          uniqueElementsMap.set(elementId, addAlternativeCompName(comp, a11yWizardAdapter, editorHost, execResult.metadata, pageId));
        }
      });
    });
    const failedElements = Array.from(uniqueElementsMap.values());
    const uiStatus = failedElements.length > 0 ? RuleListStatus.failed : RuleListStatus.passed;
    const isRuleResolved = uiStatus !== RuleListStatus.failed;
    if (shouldUpdateRuleStatus(analysisState, uiStatus, ruleState, routerId)) {
      setRuleState(pageId, RuleId.INACCESSIBLE_COMPONENTS, {
        status: uiStatus
      });
    }
    if (elements.length === 0 && failedElements.length === 0 && !inaccessibleComponentsPages.includes(pageId)) {
      return null;
    }
    return {
      description: /*#__PURE__*/React.createElement(RuleDescriptionComponent, {
        i18nKey: isRuleResolved ? 'accessibility-wizard.rules-list.inaccessible-components.description' : 'accessibility-wizard.rules-list.inaccessible-components.description.failed',
        openArticle: () => {
          openSelectedArticle(editorHost, 'editor-package-accessibility-wizard.articles-inaccessible-components-help');
          biLogger.taskCtaClick({
            ctaName: BiCtaNames.LearnMore,
            elementCount: elements.length,
            failedElementCount: failedElements.length
          });
        },
        title: isRuleResolved ? t('accessibility-wizard.rules-list.issue-resolved') : ''
      }),
      elementCount: elements.length,
      failedElementCount: failedElements.length,
      fixer: FixerType.Custom,
      fixerProps: {
        customFixer: /*#__PURE__*/React.createElement(InaccessibleComponentsRuleFixer, {
          elements: elements,
          failedElements: failedElements,
          pageId: pageId
        })
      },
      id: `${routerId ?? pageId}-${RuleId.INACCESSIBLE_COMPONENTS}`,
      label: t('accessibility-wizard.rules-list.inaccessible-components.title'),
      onRuleToggle: function (toggleType, ruleIndex, initiatedByUser) {
        if (initiatedByUser === void 0) {
          initiatedByUser = false;
        }
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: uiStatus,
          task_index: ruleIndex,
          task_name: RuleId.INACCESSIBLE_COMPONENTS
        }));
        biLogger.taskExpandCollapse({
          elementCount: elements.length,
          failedElementCount: failedElements.length,
          initiatedByUser,
          taskInfo: {
            ...currentTaskInfo,
            page_type: getPageTypeForBi(itemType, isCrossPage),
            status: uiStatus,
            task_index: ruleIndex,
            task_name: RuleId.INACCESSIBLE_COMPONENTS
          },
          toggleType
        });
        if (toggleType === ToggleType.Expand) {
          navigateToPage(pageId, routerId);
        }
      },
      onStatusChange: () => {
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: uiStatus
        }));
        biLogger.taskStatusChange({
          elementCount: elements.length,
          failedElementCount: failedElements.length,
          status: uiStatus
        });
      },
      parentGroupId: pageId,
      ruleId: RuleId.INACCESSIBLE_COMPONENTS,
      status: uiStatus
    };
  }, [getRuleState, getGroupData, openSelectedArticle, sessionComponents, t, a11yWizardAdapter, editorHost, setRuleState, biLogger, setCurrentTaskInfo, currentTaskInfo, navigateToPage, getIsNotApplicable, inaccessibleComponentsPages]);
  return {
    getInaccessibleComponents
  };
};
const addAlternativeCompName = (element, a11yWizardAdapter, editorHost, metadata, pageId) => {
  const signInPageId = a11yWizardAdapter.getMembersCustomPages().signIn.id;
  const signUpPageId = a11yWizardAdapter.getMembersCustomPages().signUp.id;
  const isSignInPage = pageId === signInPageId;
  const isSignUpPage = pageId === signUpPageId;
  const isCustomMembersPage = isSignInPage || isSignUpPage;
  const inaccessibleComponentKey = Object.keys(INACCESSIBLE_COMPONENTS).find(key => INACCESSIBLE_COMPONENTS[key].componentType === element.type);
  const isATpaComponent = isTpaComponent(element.type);
  const alternative = inaccessibleComponentKey ? isATpaComponent ? element.tpaId ? TPA_ALTERNATIVES[getTpaNameById(element.tpaId) ?? ''] : undefined : INACCESSIBLE_COMPONENTS_ALTERNATIVES[inaccessibleComponentKey] : undefined;
  const alternateComponent = alternative ? ALTERNATIVE_COMPONENTS[alternative].key : undefined;
  const reinstall = Object.values(REINSTALL_COMPONENTS_TYPES).some(type => type === element.type);
  const followArticle = Object.values(FOLLOW_ARTICLE_COMPONENTS_TYPES).some(type => type === element.type) && (!element.tpaId || Object.values(FOLLOW_ARTICLE_TPA_IDS).some(id => id === element.tpaId)) || isCustomMembersPage;
  return {
    ...element,
    alternateComponent,
    breakpointId: getAxeBreakpointId(a11yWizardAdapter, editorHost, metadata, element.id),
    followArticle,
    reinstall
  };
};
//# sourceMappingURL=use-inaccessible-components-rule.js.map